import { Endpoints } from 'src/constants/endpoints'
import {
  ComposedInvoiceStatus,
  ComposedInvoicesTableFields,
  InvoiceActionVerb,
  PaymentReleaseActionKind,
  PaymentReleaseDateProblem,
  PaymentReleaseErrorKind,
  PaymentReleaseStatus,
  PartnerInvoiceStatus,
  PartnerInvoiceType,
  PartnerInvoicesTableFields,
} from './enums'

/**
 * A `Record` over the verb enum rather than a built URL string: adding a verb without an
 * endpoint fails typechecking, and no path is ever concatenated at a call site.
 */
export const INVOICE_ACTION_ENDPOINTS: Record<InvoiceActionVerb, Endpoints> = {
  [InvoiceActionVerb.Cancel]: Endpoints.AdminInvoiceCancel,
  [InvoiceActionVerb.MarkFunded]: Endpoints.AdminInvoiceMarkFunded,
  [InvoiceActionVerb.MarkFundedNoCheck]: Endpoints.AdminInvoiceMarkFundedNoCheck,
  [InvoiceActionVerb.ProcessPayment]: Endpoints.AdminInvoiceProcessPayment,
  [InvoiceActionVerb.MarkRepaid]: Endpoints.AdminInvoiceRepayment,
  [InvoiceActionVerb.RepayFees]: Endpoints.AdminInvoiceFeesRepayment,
  [InvoiceActionVerb.RepayDueInterest]: Endpoints.AdminInvoiceRepayDueInterest,
}

export const invoiceActionLabels: Record<InvoiceActionVerb, string> = {
  [InvoiceActionVerb.Cancel]: 'Cancel invoices',
  [InvoiceActionVerb.MarkFunded]: 'Mark funded',
  [InvoiceActionVerb.MarkFundedNoCheck]: 'Mark funded (no check)',
  [InvoiceActionVerb.ProcessPayment]: 'Process payments',
  [InvoiceActionVerb.MarkRepaid]: 'Mark repaid',
  [InvoiceActionVerb.RepayFees]: 'Repay invoice fees',
  [InvoiceActionVerb.RepayDueInterest]: 'Repay due interest',
}

/**
 * The sentence shown in the confirm step, before the invoice count. Each states what actually
 * changes — never "are you sure?". `{count}` is substituted by the confirm step.
 */
export const invoiceActionConsequences: Record<InvoiceActionVerb, string> = {
  [InvoiceActionVerb.Cancel]:
    'Cancelling stops funding and repayment for {count}. This is the highest-impact action here and cannot be undone.',
  [InvoiceActionVerb.MarkFunded]:
    'This records that funding has been sent for {count}. It cannot be undone.',
  [InvoiceActionVerb.MarkFundedNoCheck]:
    'This marks {count} as funded WITHOUT checking that external funding completed, and forces the loan funding status to Succeeded. Use it only when you have confirmed funding another way.',
  [InvoiceActionVerb.ProcessPayment]:
    'This starts contractor payouts for {count}. Money moves as a result.',
  [InvoiceActionVerb.MarkRepaid]: 'This records repayment of the loan behind {count}.',
  [InvoiceActionVerb.RepayFees]:
    'This repays the outstanding fees on {count}. Only loan invoices whose principal is already settled can do this.',
  [InvoiceActionVerb.RepayDueInterest]:
    "This pays each invoice's base fee against the GigSafe-wide due-interest balance, for {count}. An invoice whose fee exceeds the remaining balance is left untouched and still reports success.",
}

/** Fallback for `resolveErrorMessage` when the backend sends nothing usable. */
export const invoiceActionFallbackMessages: Record<InvoiceActionVerb, string> = {
  [InvoiceActionVerb.Cancel]: 'Failed to cancel invoice',
  [InvoiceActionVerb.MarkFunded]: 'Failed to mark invoice funded',
  [InvoiceActionVerb.MarkFundedNoCheck]: 'Failed to mark invoice funded',
  [InvoiceActionVerb.ProcessPayment]: 'Failed to process payment',
  [InvoiceActionVerb.MarkRepaid]: 'Failed to mark invoice repaid',
  [InvoiceActionVerb.RepayFees]: 'Failed to repay invoice fees',
  [InvoiceActionVerb.RepayDueInterest]: 'Failed to repay due interest',
}

export const partnerInvoiceStatusLabels: Record<PartnerInvoiceStatus, string> = {
  [PartnerInvoiceStatus.Created]: 'Created',
  [PartnerInvoiceStatus.Pending]: 'Pending',
  [PartnerInvoiceStatus.Canceled]: 'Canceled',
  [PartnerInvoiceStatus.Funded]: 'Funded',
  [PartnerInvoiceStatus.RepaymentPending]: 'Repayment pending',
  [PartnerInvoiceStatus.Repaid]: 'Repaid',
}

export const composedInvoiceStatusLabels: Record<ComposedInvoiceStatus, string> = {
  [ComposedInvoiceStatus.Canceled]: 'Canceled',
  [ComposedInvoiceStatus.FundingFailed]: 'Funding failed',
  [ComposedInvoiceStatus.FundedAndPaid]: 'Funded & paid',
  [ComposedInvoiceStatus.Repaying]: 'Repaying',
  [ComposedInvoiceStatus.AwaitingFunding]: 'Awaiting funding',
}

export const partnerInvoiceTypeLabels: Record<PartnerInvoiceType, string> = {
  [PartnerInvoiceType.Wire]: 'Wire',
  [PartnerInvoiceType.Loan]: 'Loan',
  [PartnerInvoiceType.Historical]: 'Historical',
}

export const composedInvoiceTableHeaderTitles: Record<ComposedInvoicesTableFields, string> = {
  [ComposedInvoicesTableFields.invoice]: 'Invoice',
  [ComposedInvoicesTableFields.type]: 'Type',
  [ComposedInvoicesTableFields.status]: 'Status',
  [ComposedInvoicesTableFields.amount]: 'Amount',
  [ComposedInvoicesTableFields.lateFee]: 'Late fee',
  [ComposedInvoicesTableFields.generated]: 'Invoice generated',
  [ComposedInvoicesTableFields.paymentRelease]: 'Payment release',
  // The actions column is a single overflow button; a header would just add noise.
  [ComposedInvoicesTableFields.actions]: '',
}

export const partnerInvoiceTableHeaderTitles: Record<PartnerInvoicesTableFields, string> = {
  [PartnerInvoicesTableFields.invoice]: 'Invoice ID',
  [PartnerInvoicesTableFields.status]: 'Status',
  [PartnerInvoicesTableFields.type]: 'Type',
  [PartnerInvoicesTableFields.amount]: 'Amount',
  [PartnerInvoicesTableFields.paidAmount]: 'Paid amount',
  [PartnerInvoicesTableFields.invoiceDate]: 'Invoice date',
  [PartnerInvoicesTableFields.dueDate]: 'Due date',
  [PartnerInvoicesTableFields.earningsCount]: 'Earnings',
  [PartnerInvoicesTableFields.paymentRelease]: 'Payment release',
  [PartnerInvoicesTableFields.actions]: '',
  [PartnerInvoicesTableFields.externalStatus]: 'External status',
  [PartnerInvoicesTableFields.loanFundingStatus]: 'Loan funding status',
  [PartnerInvoicesTableFields.loanFundingErrorMessage]: 'Loan funding error',
  [PartnerInvoicesTableFields.paymentStatus]: 'Payment status',
  [PartnerInvoicesTableFields.paymentErrorMessage]: 'Payment error',
  [PartnerInvoicesTableFields.paymentDate]: 'Payment date',
  [PartnerInvoicesTableFields.payInvoiceOnDate]: 'Pay invoice on',
  [PartnerInvoicesTableFields.loanId]: 'Loan ID',
  [PartnerInvoicesTableFields.loanDueDate]: 'Loan due date',
  [PartnerInvoicesTableFields.loanTotalAmount]: 'Loan total',
  [PartnerInvoicesTableFields.loanBaseFeeAmount]: 'Loan base fee',
  [PartnerInvoicesTableFields.loanTransferFee]: 'Loan transfer fee',
  [PartnerInvoicesTableFields.loanTransferRate]: 'Loan transfer rate',
  [PartnerInvoicesTableFields.wireTransferFee]: 'Wire transfer fee',
  [PartnerInvoicesTableFields.wireTransferRate]: 'Wire transfer rate',
  [PartnerInvoicesTableFields.gigsafeTotalFeeAmount]: 'GigSafe total fee',
  [PartnerInvoicesTableFields.invoiceApprovedByPayor]: 'Approved by payor',
  [PartnerInvoicesTableFields.payorExternalId]: 'Payor external ID',
  [PartnerInvoicesTableFields.payeeExternalId]: 'Payee external ID',
  [PartnerInvoicesTableFields.paymentId]: 'Payment ID',
  [PartnerInvoicesTableFields.description]: 'Description',
}

/**
 * Server-paginated, so the first value is the initial `limit` sent to the API. Kept small:
 * every row expands into a nested table, so a 100-row page would be unusable.
 */
export const invoicePageSizeOptions = [10, 25, 50]

/** Typed by the admin to unlock the Cancel confirm button. Compared case-insensitively. */
export const INVOICE_CANCEL_CONFIRMATION_WORD = 'CANCEL'

export const COMPOSED_INVOICES_GRID_COLUMNS_STORAGE_KEY = 'admin.invoices.composedGridColumns'
export const PARTNER_INVOICES_GRID_COLUMNS_STORAGE_KEY = 'admin.invoices.partnerGridColumns'

/**
 * `releaseAt` is a wall clock in THIS zone wherever the admin is sitting, and the scheduled
 * instant is rendered back in it. Central Time is the business's clock, not a display choice.
 */
export const PAYMENT_RELEASE_TIME_ZONE = 'America/Chicago'

/** Luxon tokens for the `YYYY-MM-DD HH:mm` wire format of `releaseAt`. */
export const PAYMENT_RELEASE_AT_FORMAT = 'yyyy-MM-dd HH:mm'

/**
 * The furthest ahead a hold can be placed. A Cloud Tasks limit, not a product choice — it will
 * not be raised. Counted in hours, not calendar days: 30 days across a fall-back transition is
 * 721 hours, which Cloud Tasks would reject.
 */
export const PAYMENT_RELEASE_MAX_HOURS_AHEAD = 30 * 24

export const paymentReleaseStatusLabels: Record<PaymentReleaseStatus, string> = {
  [PaymentReleaseStatus.Scheduled]: 'Scheduled',
  [PaymentReleaseStatus.Processing]: 'Releasing',
  [PaymentReleaseStatus.Released]: 'Released',
  [PaymentReleaseStatus.Failed]: 'Release failed',
}

/** Shown on hover/focus of the status chip, so the meaning is not left to the label alone. */
export const paymentReleaseStatusDescriptions: Record<PaymentReleaseStatus, string> = {
  [PaymentReleaseStatus.Scheduled]:
    'Driver payments are held until this time (Central Time). Process payments still pays out immediately if run by hand.',
  [PaymentReleaseStatus.Processing]:
    'The release is running right now. It cannot be rescheduled or cancelled. If it stays here, escalate to engineering — a stuck release is cleared by hand.',
  [PaymentReleaseStatus.Released]:
    'Handed to payment processing. This does not mean the payments have settled.',
  [PaymentReleaseStatus.Failed]:
    'The release failed and ops were alerted. Nothing retries automatically — it needs someone to resolve it.',
}

export const paymentReleaseActionLabels: Record<PaymentReleaseActionKind, string> = {
  [PaymentReleaseActionKind.Schedule]: 'Schedule payment release',
  [PaymentReleaseActionKind.Reschedule]: 'Reschedule payment release',
  [PaymentReleaseActionKind.Cancel]: 'Cancel scheduled release',
}

export const paymentReleaseDateProblemMessages: Record<PaymentReleaseDateProblem, string> = {
  [PaymentReleaseDateProblem.Invalid]: 'Enter a valid date and time',
  [PaymentReleaseDateProblem.Past]: 'Pick a time in the future (Central Time)',
  [PaymentReleaseDateProblem.TooFar]: 'Pick a time no more than 30 days from now',
}

/**
 * Admin-facing copy per failure, chosen by the error NAME. The three 409s mean entirely
 * different things and each says what to do next — never one generic "conflict".
 * `Unknown` has no entry: it surfaces the server's own message.
 */
export const paymentReleaseErrorMessages: Record<
  Exclude<PaymentReleaseErrorKind, PaymentReleaseErrorKind.Unknown>,
  string
> = {
  [PaymentReleaseErrorKind.Validation]:
    'The release time was not accepted. Pick the date and time again.',
  [PaymentReleaseErrorKind.Auth]: 'Your session has expired. Sign in again, then retry.',
  [PaymentReleaseErrorKind.Permission]:
    'You do not have permission to manage invoice payment releases.',
  [PaymentReleaseErrorKind.InvoiceNotFound]:
    'This invoice was not found for this customer. The list has been refreshed.',
  [PaymentReleaseErrorKind.InvalidReleaseDate]:
    'The release time must be in the future and no more than 30 days ahead. Pick another time.',
  [PaymentReleaseErrorKind.ReleaseInProgress]:
    'This release is running right now, so it can no longer be rescheduled or cancelled. The list has been refreshed.',
  [PaymentReleaseErrorKind.AutoReleaseOnFundingEnabled]:
    "This customer releases payments automatically when an invoice is funded. Turn that off in the customer's payment settings first, then schedule again.",
  [PaymentReleaseErrorKind.NoPaymentsAwaitingRelease]:
    'Every payment on this invoice has already gone out, so there is nothing left to hold. The list has been refreshed.',
  [PaymentReleaseErrorKind.NoScheduledRelease]:
    'This invoice no longer has a scheduled release — it may have just run or been cancelled. The list has been refreshed.',
}

export const paymentReleaseFallbackMessages: Record<PaymentReleaseActionKind, string> = {
  [PaymentReleaseActionKind.Schedule]: 'Failed to schedule the payment release',
  [PaymentReleaseActionKind.Reschedule]: 'Failed to reschedule the payment release',
  [PaymentReleaseActionKind.Cancel]: 'Failed to cancel the scheduled release',
}
